export type NavPanelTool = {
  label: string;
  href: string;
  description?: string;
};

export type NavWorkspace = {
  type: "workspace";
  label: string;
  href: string;
  category: "market" | "flow" | "scanner" | "strategies" | "news" | "more";
  panelTools: NavPanelTool[];
};

export type NavDirectLink = {
  type: "link";
  label: string;
  href: string;
};

export type PrimaryNavItem = NavWorkspace | NavDirectLink;

export type NavUtilityItem = {
  label: string;
  href: string;
};

export type SearchItem = {
  label: string;
  href: string;
  description?: string;
  category?: string;
};

export const primaryNav: PrimaryNavItem[] = [
  {
    type: "workspace",
    label: "Market",
    href: "/market-overview",
    category: "market",
    panelTools: [
      { label: "Index Dashboard", href: "/indices/KSE-100", description: "KSE-100 live workspace" },
      { label: "Market Leaders", href: "/market/leaders", description: "Daily and weekly leaders across volume, value, benchmark outperformance" },
      { label: "Relative Strength", href: "/indices/relative-strength", description: "Strong stocks vs index" },
      { label: "Pressure Index", href: "/market/pressure-index", description: "Composite stress gauge" },
    ],
  },
  {
    type: "workspace",
    label: "Money Flow",
    href: "/money-flow/insights",
    category: "flow",
    panelTools: [
      { label: "Off-Market Transactions", href: "/money-flow/off-market", description: "Negotiated block trades" },
      { label: "Leverage & Short Pressure", href: "/money-flow/funding", description: "Futures, margins & blank sale" },
      { label: "Settlement Confirmation", href: "/money-flow/settlement", description: "UIN and retail settlement confirmation" },
      { label: "Participant Flow (FIPI/LIPI)", href: "/money-flow/flows", description: "FIPI/LIPI pressure, retail, and sector flow" },
    ],
  },
  {
    type: "link",
    label: "Scanner",
    href: "/scanner",
  },
  {
    type: "link",
    label: "Strategies",
    href: "/strategies",
  },
  {
    type: "workspace",
    label: "More",
    href: "/more",
    category: "more",
    panelTools: [
      { label: "Company Journey Game", href: "/company-journey", description: "Learn EPS, ROE, debt, book value and owner returns through a simple story" },
      { label: "Portfolio", href: "/portfolio", description: "Track holdings, watchlist and dividend income" },
      { label: "Trade Journal", href: "/trade-journal", description: "Record entries, exits, notes and outcomes" },
      { label: "News", href: "/news", description: "Pakistan business and PSX headlines" },
      { label: "Learning", href: "/learning", description: "PSX concepts, guides and tutorials" },
      { label: "PSX Glossary", href: "/glossary", description: "FIPI, UIN, blank sale, rollover and other PSX terms explained" },
      { label: "Calculator", href: "/calculator", description: "Average cost and PSX return calculators" },
      { label: "Daily Tracker", href: "/daily-tracker", description: "Session plan and daily trading checklist" },
      { label: "Alerts", href: "/alerts", description: "Unified signal feed ranked by conviction" },
    ],
  },
];

export const utilityNav: NavUtilityItem[] = [
  { label: "Learning", href: "/learning" },
  { label: "Calculator", href: "/calculator" },
  { label: "Contact", href: "/contact" },
  { label: "About", href: "/about" },
];

export const frequencySensitiveNav: NavUtilityItem[] = [
  { label: "Daily Tracker", href: "/daily-tracker" },
  { label: "Alerts", href: "/alerts" },
  { label: "Portfolio", href: "/portfolio" },
  { label: "Trade Journal", href: "/trade-journal" },
];

export const commandPaletteIndex: SearchItem[] = [
  // Market
  { label: "Market Overview", href: "/market-overview", description: "Breadth & session read", category: "Market" },
  { label: "Market Leaders", href: "/market/leaders", description: "Daily and weekly leaders across volume, value, benchmark outperformance", category: "Market" },
  { label: "Index Dashboard", href: "/indices/KSE-100", description: "KSE-100 live workspace", category: "Market" },
  { label: "Index Fluctuation", href: "/indices/fluctuation", description: "PSX intraday and one-year index movement", category: "Market" },
  { label: "Relative Strength (Strong Stocks)", href: "/indices/relative-strength", description: "Stocks beating their index", category: "Market" },
  { label: "Pressure Index", href: "/market/pressure-index", description: "Composite stress gauge", category: "Market" },
  // Money Flow
  { label: "Insights", href: "/money-flow/insights", description: "Daily confluence board", category: "Money Flow" },
  { label: "Off-Market Transactions", href: "/money-flow/off-market", description: "Negotiated block trades", category: "Money Flow" },
  { label: "Leverage & Short Pressure", href: "/money-flow/funding", description: "Futures, margins & blank sale", category: "Money Flow" },
  { label: "Settlement Confirmation", href: "/money-flow/settlement", description: "UIN and retail settlement confirmation", category: "Money Flow" },
  { label: "Participant Flow (FIPI/LIPI)", href: "/money-flow/flows", description: "FIPI/LIPI pressure, retail, and sector flow", category: "Money Flow" },
  // Scanner
  { label: "Liquidity Sweep", href: "/scanner/liquidity-sweep", description: "Previous-session sweeps ranked by percentage", category: "Scanner · Patterns" },
  { label: "Scanner", href: "/scanner", description: "All scanners by category", category: "Scanner" },
  { label: "Fundamental Comparison", href: "/fundamentals/sector-comparison", description: "Compare earnings, revenue, equity growth and ROE by sector", category: "Scanner · Fundamentals" },
  { label: "Momentum Pulse", href: "/scanner/pulse", description: "RSI + MACD combined conviction", category: "Scanner · Momentum" },
  { label: "RSI Cross", href: "/scanner/rsi", description: "Threshold reclaim signals", category: "Scanner · Momentum" },
  { label: "MACD Turn", href: "/scanner/macd", description: "Histogram turns positive", category: "Scanner · Momentum" },
  { label: "EMA Status", href: "/scanner/advanced", description: "Full EMA matrix across timeframes", category: "Scanner · Trend" },
  { label: "Near Key MAs", href: "/scanner/ma", description: "Stocks near EMA/SMA support", category: "Scanner · Trend" },
  { label: "Trendline Support", href: "/scanner/trendline-support", description: "Stocks near auto-detected support trendlines", category: "Scanner · Trend" },
  { label: "Resistance Breakouts", href: "/scanner/trendline-resistance-breakout", description: "Stocks breaking auto-detected resistance trendlines", category: "Scanner · Trend" },
  { label: "Price Action", href: "/scanner/price-action", description: "Breakouts & structure setups", category: "Scanner · Structure" },
  { label: "Elliott Impulse", href: "/scanner/elliott", description: "Impulse trend structure", category: "Scanner · Structure" },
  { label: "Renko P&F", href: "/scanner/renko-pnf", description: "Brick-based structure", category: "Scanner · Structure" },
  { label: "Point & Figure", href: "/scanner/pnf", description: "Column breakout charts", category: "Scanner · Structure" },
  { label: "Alerts", href: "/alerts", description: "Unified signal feed ranked by conviction", category: "Scanner · Setups" },
  { label: "Smart Scan", href: "/scanner/smart-scan", description: "Bullish confluence and defensible longs", category: "Scanner · Setups" },
  { label: "Next-Day Setups", href: "/scanner/premium", description: "Top-scored composite longs", category: "Scanner · Setups" },
  { label: "Renko First Green", href: "/scanner/renko-first-green", description: "Reversals with volume context", category: "Scanner · Setups" },
  { label: "Volume Anomalies", href: "/scanner/volume", description: "Unusual volume activity", category: "Scanner · Volume" },
  { label: "Volume Divergence", href: "/scanner/volume-divergence", description: "Price vs OBV and absorption signals", category: "Scanner · Volume" },
  { label: "Volume Profile", href: "/scanner/volume-profile", description: "POC, VAH/VAL, HVN/LVN profile states", category: "Scanner · Volume" },
  { label: "Volume Intelligence", href: "/scanner/volume-intelligence", description: "Combined private volume scanner", category: "Scanner · Volume" },
  { label: "Klinger Oscillator", href: "/scanner/klinger", description: "Volume-force trend turns", category: "Scanner · Volume" },
  { label: "Pattern Scanner", href: "/data/patterns", description: "Bullish, bearish & contextual", category: "Scanner · Patterns" },
  { label: "RSI Divergences", href: "/data/divergences", description: "Momentum divergence signals", category: "Scanner · Patterns" },
  { label: "Doji Scanner", href: "/data/doji", description: "Doji and exhaustion setups", category: "Scanner · Patterns" },
  { label: "Reversal Scanner", href: "/data/reversal", description: "Reversal pattern groups", category: "Scanner · Patterns" },
  { label: "Trend Structure", href: "/data/structure", description: "Structure vs candle alignment", category: "Scanner · Patterns" },
  // Strategies
  { label: "Strategy Workspace", href: "/strategies", description: "Live triggers & backtests", category: "Strategies" },
  { label: "Next-Day Potential", href: "/strategies/next-day-potential", description: "Next-session momentum playbook", category: "Strategies" },
  { label: "Beginner Setups", href: "/strategies?level=beginner", description: "Simple, easy-to-read playbooks", category: "Strategies" },
  { label: "Intermediate Setups", href: "/strategies?level=intermediate", description: "Confirmation-led entries", category: "Strategies" },
  { label: "Advanced Setups", href: "/strategies?level=advanced", description: "Confluence & scanner-led", category: "Strategies" },
  // News & Tools
  { label: "News", href: "/news", description: "PSX market news", category: "News" },
  { label: "Daily Tracker", href: "/daily-tracker", description: "Session plan & log", category: "Tools" },
  { label: "Alerts", href: "/alerts", description: "Signal feed", category: "Tools" },
  { label: "Portfolio", href: "/portfolio", description: "Watchlist & holdings", category: "Tools" },
  { label: "Trade Journal", href: "/trade-journal", description: "Entries, exits & notes", category: "Tools" },
  { label: "Learning", href: "/learning", description: "Concepts behind the tools", category: "Learning" },
  { label: "PSX Glossary", href: "/glossary", description: "FIPI, UIN, blank sale, rollover — PSX terms explained", category: "Learning" },
  { label: "Company Journey Game", href: "/company-journey", description: "Learn IPOs, ROE, ROI, EPS, debt and dividends through decisions", category: "Learning" },
  { label: "Calculator", href: "/calculator", description: "Position & risk sizing", category: "Tools" },
  { label: "Signal Learning", href: "/signal-learning", description: "Internal scanner performance and delivery research", category: "Internal" },
];
